import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from 'react'
import { fetchUserPermissions } from 'services/commonService'

export type Permission = {
    module: string
    feature: string
}

type Feature = {
    featureId: number
    featureCode: string
    featureName: string
}

const PermissionContext = createContext<{
    permissions: Permission[]
    loading: boolean
}>({
    permissions: [],
    loading: true,
})

export const usePermissions = () => useContext(PermissionContext)

type PermissionProviderProps = {
    userEmail: string
    children: ReactNode
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
    userEmail,
    children,
}) => {
    const [permissions, setPermissions] = useState<Permission[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const loadPermissions = async () => {
            setLoading(true)

            if (!userEmail) {
                console.warn('PermissionProvider: userEmail is missing')
                setLoading(false)
                return
            }

            const response = await fetchUserPermissions(userEmail)

            const rawModules = response?.data?.data
            if (!Array.isArray(rawModules)) {
                console.warn(
                    '⚠️ Permissions API did not return expected format:',
                    response
                )
                setLoading(false)
                return
            }

            const flatPermissions: Permission[] = rawModules.flatMap(
                (module) => {
                    if (!module.features || module.features.length === 0) {
                        return [
                            {
                                module: module.moduleName,
                                feature: '',
                            },
                        ]
                    }

                    return module.features.map((f: Feature) => ({
                        module: module.moduleName,
                        feature: f.featureName,
                    }))
                }
            )

            console.log('✅ Transformed Permissions:', flatPermissions)
            setPermissions(flatPermissions)
            setLoading(false)
        }

        loadPermissions()
    }, [userEmail])

    return (
        <PermissionContext.Provider value={{ permissions, loading }}>
            {!loading && children}
        </PermissionContext.Provider>
    )
}
